import { MODIFICATION_REQUEST_MIN_LENGTH } from '@massilia/queries'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@massilia/ui'
import { useState } from 'react'

interface RequestModificationDialogProps {
  responseName: string
  isOpen: boolean
  isSubmitting: boolean
  onConfirm: (payload: { message: string }) => void
  onOpenChange: (open: boolean) => void
}

/**
 * Demande d'ajustement. Ne gèle rien : la proposition reste confirmable ou
 * refusable ensuite, et le client peut en enchaîner plusieurs.
 */
export function RequestModificationDialog({
  responseName,
  isOpen,
  isSubmitting,
  onConfirm,
  onOpenChange,
}: RequestModificationDialogProps) {
  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      {/*
       * Remonté à chaque OUVERTURE, par la `key` : Radix masque le contenu à la
       * fermeture au lieu de le démonter, et le texte de la demande précédente
       * restait dans la zone de saisie — deux demandes d'affilée, et la seconde
       * repartait avec le texte de la première. Un remontage plutôt qu'un effet
       * de remise à zéro, qui ferait un rendu en cascade à chaque ouverture.
       */}
      <RequestModificationDialogBody
        isSubmitting={isSubmitting}
        key={String(isOpen)}
        onConfirm={onConfirm}
        onOpenChange={onOpenChange}
        responseName={responseName}
      />
    </Dialog>
  )
}

function RequestModificationDialogBody({
  responseName,
  isSubmitting,
  onConfirm,
  onOpenChange,
}: Omit<RequestModificationDialogProps, 'isOpen'>) {
  const [message, setMessage] = useState('')

  const canSubmit = message.trim().length >= MODIFICATION_REQUEST_MIN_LENGTH

  return (
    <DialogContent fullScreenOnMobile>
      <DialogHeader>
        <DialogTitle>Demander des modifications sur « {responseName} »</DialogTitle>
        <DialogDescription>
          Par exemple : partir quelques jours plus tard, une autre compagnie, un hôtel différent, un
          budget à revoir.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="modification-message">Ce que vous souhaitez modifier</Label>
        <Textarea
          id="modification-message"
          maxLength={2000}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Décrivez en quelques mots ce que vous aimeriez changer."
          rows={5}
          value={message}
        />
        {!canSubmit && message.length > 0 && (
          <p className="text-xs text-gray-light-600">
            Encore quelques mots : {MODIFICATION_REQUEST_MIN_LENGTH} caractères minimum.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
          Annuler
        </Button>
        <Button
          className="bg-brand-600 text-white hover:bg-brand-700"
          disabled={!canSubmit || isSubmitting}
          onClick={() => onConfirm({ message: message.trim() })}
          type="button"
        >
          Envoyer ma demande
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
